import React, { createContext, useContext, useEffect, useState } from 'react';
import { dbManager, CachedTranscription } from '../utils/IndexedDBManager';
import { useToast } from './ToastContext';

interface TranscriptionCacheContextType {
    getCachedTranscription: (id: string) => Promise<CachedTranscription | null>;
    cacheTranscription: (transcription: CachedTranscription) => Promise<void>;
    clearCache: () => Promise<void>;
}

const TranscriptionCacheContext = createContext<TranscriptionCacheContextType | null>(null);

export function TranscriptionCacheProvider({ children }: { children: React.ReactNode }) {
    const { showToast } = useToast();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeDB = async () => {
            try {
                await dbManager.init();
                await dbManager.clearOldTranscriptions(); // Clear transcriptions older than 7 days
                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to initialize IndexedDB:', error);
                showToast('Failed to initialize cache system', 'error');
            }
        };

        initializeDB();
    }, [showToast]);

    const getCachedTranscription = async (id: string): Promise<CachedTranscription | null> => {
        if (!isInitialized) return null;
        
        try {
            return await dbManager.getTranscription(id);
        } catch (error) {
            console.error('Failed to retrieve cached transcription:', error);
            showToast('Failed to retrieve cached transcription', 'error');
            return null;
        }
    };

    const cacheTranscription = async (transcription: CachedTranscription): Promise<void> => {
        if (!isInitialized) return;
        
        try {
            await dbManager.saveTranscription(transcription);
        } catch (error) {
            console.error('Failed to cache transcription:', error);
            showToast('Failed to save transcription to cache', 'error');
        }
    };

    const clearCache = async (): Promise<void> => {
        if (!isInitialized) return;
        
        try {
            await dbManager.clearOldTranscriptions(0); // Clear all transcriptions
            showToast('Cache cleared successfully', 'success');
        } catch (error) {
            console.error('Failed to clear cache:', error);
            showToast('Failed to clear cache', 'error');
        }
    };

    const value = {
        getCachedTranscription,
        cacheTranscription,
        clearCache,
    };

    return (
        <TranscriptionCacheContext.Provider value={value}>
            {children}
        </TranscriptionCacheContext.Provider>
    );
}

export function useTranscriptionCache() {
    const context = useContext(TranscriptionCacheContext);
    if (!context) {
        throw new Error('useTranscriptionCache must be used within a TranscriptionCacheProvider');
    }
    return context;
}
